import {
  HttpEvent,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { parseISO, isValid } from 'date-fns';

/**
 * apiResponseInterceptor
 *
 * This Angular HTTP interceptor automatically parses ISO date strings
 * in API responses and converts them into JavaScript Date objects.
 */
export const apiResponseInterceptor: HttpInterceptorFn = (req, next): Observable<HttpEvent<any>> => {
  return next(req).pipe(
    map((evt: HttpEvent<any>) => {
      // Intercept only complete HTTP responses
      if (evt instanceof HttpResponse) {
        // Clone the response and parse date strings in the body
        evt = evt.clone({
          body: parseDates(evt.body),
        });
      }
      return evt;
    })
  );
};

/**
 * Recursively parses an object, array, or primitive.
 * If a string matches ISO 8601 date format, it converts it to a Date.
 */
function parseDates(obj: any): any {
  if (Array.isArray(obj)) {
    // If it's an array, recursively parse each element
    return obj.map((value) => parseDates(value));
  } else if (obj !== null && typeof obj === 'object') {
    // If it's an object, recursively parse each property
    const parsed: any = {};
    for (const key of Object.keys(obj)) {
      parsed[key] = parseDates(obj[key]);
    }
    return parsed;
  } else if (typeof obj === 'string' && isIsoDate(obj)) {
    // If it's an ISO date string, parse to Date object
    const date = parseISO(obj);
    return isValid(date) ? date : obj;
  }
  // Return the original value if it's not a date string
  return obj;
}

/**
 * Checks if a string is in ISO 8601 date format.
 */
function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(value);
}
